/**
 * Translation Cache Utility
 *
 * With Chained Backend, localStorage is the primary cache (i18next-localstorage-backend).
 * This module keeps an in-memory cache for backward compatibility and provides
 * clearTranslationCache() for dev tools - which also clears localStorage entries.
 *
 * localStorage key format (i18next-localstorage-backend): `i18next_res_{lng}_{ns}`
 */

const LOCALSTORAGE_PREFIX = 'i18next_res_'

type CacheEntry = {
  data: Record<string, unknown>
  timestamp: number
}

class TranslationCache {
  private cache: Map<string, CacheEntry> = new Map()

  /**
   * Get cached translation data
   */
  get(language: string, namespace: string): Record<string, unknown> | null {
    const key = this.getKey(language, namespace)
    const entry = this.cache.get(key)
    return entry ? entry.data : null
  }

  /**
   * Set cached translation data
   */
  set(language: string, namespace: string, data: Record<string, unknown>): void {
    const key = this.getKey(language, namespace)
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    })
  }

  /**
   * Check if cache has entry
   */
  has(language: string, namespace: string): boolean {
    const key = this.getKey(language, namespace)
    return this.cache.has(key)
  }

  /**
   * Clear cache for specific language/namespace.
   * Also clears localStorage entries used by i18next-localstorage-backend.
   */
  clear(language?: string, namespace?: string): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      const keysToRemove: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i)
        if (key && key.startsWith(LOCALSTORAGE_PREFIX)) {
          const suffix = key.slice(LOCALSTORAGE_PREFIX.length)
          if (language && namespace) {
            if (suffix === `${language}_${namespace}`) keysToRemove.push(key)
          } else if (language) {
            if (suffix.startsWith(`${language}_`)) keysToRemove.push(key)
          } else {
            keysToRemove.push(key)
          }
        }
      }
      keysToRemove.forEach((k) => window.localStorage.removeItem(k))
    }

    if (language && namespace) {
      const key = this.getKey(language, namespace)
      this.cache.delete(key)
    } else if (language) {
      const prefix = `${language}:`
      for (const key of this.cache.keys()) {
        if (key.startsWith(prefix)) {
          this.cache.delete(key)
        }
      }
    } else {
      this.cache.clear()
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    }
  }

  /**
   * Generate cache key
   */
  private getKey(language: string, namespace: string): string {
    return `${language}:${namespace}`
  }
}

// Export singleton instance
export const translationCache = new TranslationCache()

/**
 * Clear translation cache - exposed for dev tools
 * Can be called via window.clearTranslationCache() in dev mode
 */
export const clearTranslationCache = (language?: string, namespace?: string): void => {
  translationCache.clear(language, namespace)
  if (import.meta.env.MODE === 'development') {
    console.log('[TranslationCache] Cache cleared', { language, namespace })
  }
}

// Expose to window in dev mode for easy access
if (typeof window !== 'undefined' && import.meta.env.MODE === 'development') {
  ;(window as unknown as Record<string, unknown>).clearTranslationCache = clearTranslationCache
  ;(window as unknown as Record<string, unknown>).getTranslationCacheStats = () => translationCache.getStats()
}
